import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, DatePicker, Select, Tooltip } from 'antd';
import dayjs from 'dayjs';
import ContentWMax from '../../Components/PageLayout/ContentWMax';
import PageTitle from '../../Components/PageTitle/PageTitle';
import api from '../../services/api';
import styles from './AnalyticsPage.module.scss';
import LoadingOverlay from '../../Components/Loading/LoadingOverlay';

const { RangePicker } = DatePicker;

const metricDefinitions = {
    tasks_total: 'Все видимые неудаленные тикеты в выбранной команде после исключений аналитики.',
    open_tasks_now: 'Открытые тикеты, которые не закрыты и не находятся в ожидании партнера, на момент генерации.',
    waiting_partner_now: 'Открытые тикеты со статусом ожидания партнера или клиента.',
    closed_tasks_period: 'Тикеты, у которых дата закрытия попадает в выбранный диапазон.',
    avg_first_response_minutes: 'Среднее рабочее время от создания тикета до первого ответа внутреннего сотрудника ТП.',
    avg_reply_after_author_minutes: 'Среднее рабочее время от сообщения автора или клиента до следующего ответа сотрудника ТП.',
    avg_close_hours: 'Среднее рабочее время от создания тикета до закрытия для тикетов, закрытых в период.',
    close_time_coefficient: 'Коэффициент среднего времени закрытия: до 8 ч — 1,4; до 12 ч — 1,2; до 16 ч — 1,0; до 24 ч — 0,8; более 24 ч — 0,6.',
    waiting_staff_now: 'Открытые тикеты, где последнее значимое сообщение было от автора или клиента.',
    waiting_staff_over_2h: 'Тикеты, ожидающие ответа ТП больше 2 рабочих часов.',
    waiting_staff_over_24h: 'Тикеты, ожидающие ответа ТП больше 24 рабочих часов.',
    unassigned_now: 'Открытые тикеты без назначенного исполнителя.',
    no_first_response_now: 'Открытые тикеты, где еще не было ответа внутреннего сотрудника.',
    inactivity_now: 'Открытые тикеты, по которым есть открытый inactivity ticket.',
    terminal_payment_blocked_now: 'Открытые тикеты с флагом "Терминал не принимает оплаты".',
    sla_first_response: 'Количество и процент тикетов по состояниям SLA первого ответа.',
    sla_resolution: 'Количество и процент тикетов по состояниям SLA срока решения.',
    sla_compliance_percent: 'Доля тикетов без просрочки SLA среди тикетов с рассчитанным SLA.',
    sla_overdue_total: 'Тикеты, где первый ответ или решение находятся в состоянии overdue.',
    age_buckets: 'Открытые тикеты, разложенные по возрасту от даты создания.',
    repeat_terminal_tasks_period: 'Тикеты за период, у которых повторяется связка терминал + категория.',
    repeat_company_tasks_period: 'Тикеты за период, у которых повторяется связка филиал + категория.',
    reopened_tasks_period: 'Тикеты, где audit-log фиксирует переход из закрытого статуса обратно в открытый.',
    reassigned_tasks_period: 'Тикеты, где audit-log фиксирует смену исполнителя.',
    avg_status_time_hours: 'Среднее время между изменениями статуса по audit-log. Для старых тикетов без истории данные частичные.',
    rating_avg_score: 'Средняя оценка закрытия тикетов по оценкам, отправленным в выбранный период.',
    rating_coefficient: 'Коэффициент средней оценки: от 4,60 — 1,4; от 4,30 — 1,2; от 4,00 — 1,0; от 3,70 — 0,8; ниже 3,70 — 0,6.',
    kpi_coefficient: 'KPI IT ТП — среднее арифметическое коэффициента времени закрытия и коэффициента оценки. Если одного показателя нет, KPI не рассчитывается.',
    ratings_submitted_period: 'Количество оценок тикетов, отправленных в выбранный период. Период считается по дате отправки оценки.',
    rating_comments_period: 'Количество отправленных оценок с непустым комментарием в выбранный период.',
    rating_pending_now: 'Текущие закрытые видимые тикеты, по которым оценка запрошена, но еще не отправлена.',
    rating_score_distribution: 'Распределение отправленных оценок по значениям от 1 до 5 за выбранный период.',
    activity_actions_period: 'Все действия сотрудников по видимым тикетам за выбранный диапазон.',
    activity_tasks_touched: 'Количество уникальных тикетов, по которым сотрудник совершал действия за выбранный период.',
    activity_task_views: 'Сколько раз сотрудник открывал карточки тикетов за выбранный период.',
    activity_task_updates: 'Сколько раз сотрудник менял поля тикетов за выбранный период.',
    activity_messages_created: 'Количество сообщений, созданных сотрудником в тикетах за выбранный период.',
    activity_messages_updated: 'Количество сообщений, отредактированных сотрудником за выбранный период.',
    activity_messages_deleted: 'Количество сообщений, удаленных сотрудником за выбранный период.',
    activity_last_at: 'Последнее действие сотрудника в audit-log внутри выбранного периода.',
    staff_without_activity_period: 'Сотрудники из выбранных команд, у которых не было действий за период.',
    close_rate_per_day: 'Количество закрытых тикетов за период, деленное на число дней в выбранном диапазоне.',
    incoming_tickets: 'Тикеты, созданные в день сводки.',
    resolved_tickets: 'Тикеты, закрытые в день сводки.',
    unresolved_within_day: 'Тикеты, созданные в день сводки и не закрытые до конца этого дня.',
    in_work_total: 'Тикеты в работе на конец дня сводки.',
    waiting_partner_total: 'Тикеты в ожидании партнера на конец дня сводки.',
    terminal_down_total: 'Тикеты с признаками терминальной проблемы на конец дня сводки.',
    tickets_over_14_days: 'Тикеты в работе на конец дня сводки, созданные более 14 дней назад.',
    tickets_over_30_days: 'Тикеты в работе на конец дня сводки, созданные более 30 дней назад.',
    breakdown_total: 'Тикеты, созданные или закрытые в период, а также текущие открытые тикеты.',
    breakdown_created: 'Тикеты, созданные в выбранный период.',
    breakdown_open: 'Текущие открытые тикеты в этой группе.',
    breakdown_closed: 'Тикеты, закрытые в выбранный период.',
    breakdown_sla_overdue: 'Тикеты группы с просрочкой первого ответа или срока решения.',
    breakdown_repeat_count: 'Количество тикетов в повторяющихся связках за период.',
    breakdown_avg_close_hours: 'Среднее рабочее время закрытия тикетов группы.',
};

const datePresets = [
    { label: '1 день', value: [dayjs(), dayjs()] },
    { label: '7 дней', value: [dayjs().subtract(6, 'day'), dayjs()] },
    { label: '30 дней', value: [dayjs().subtract(29, 'day'), dayjs()] },
    { label: '90 дней', value: [dayjs().subtract(89, 'day'), dayjs()] },
];

const slaStateLabels = {
    normal: 'Норма',
    risk: 'Риск',
    overdue: 'Просрочено',
    done: 'Готово',
    paused: 'Пауза',
};

function formatNumber(value, suffix = '') {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '—';
    }
    return `${value}${suffix}`;
}

function formatPercent(value) {
    return formatNumber(value, '%');
}

function formatCoefficient(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return '—';
    }

    return Number(value).toFixed(1).replace('.', ',');
}

function formatAnalyticsDate(value) {
    if (!value) return '—';
    const date = dayjs(value);
    return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '—';
}

function coefficientTone(value) {
    if (value === null || value === undefined || value === '') return '';
    const coefficient = Number(value);
    if (!Number.isFinite(coefficient)) return '';
    if (coefficient >= 1.3) return styles.kpiExcellent;
    if (coefficient >= 1.1) return styles.kpiGood;
    if (coefficient >= 0.9) return styles.kpiNeutral;
    if (coefficient >= 0.7) return styles.kpiWarning;
    return styles.kpiLow;
}

function formatClosedCount(value) {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return null;
    }

    return `закрыто ${value}`;
}

function ratingDistributionValue(distribution, score) {
    const value = distribution?.[score] ?? distribution?.[String(score)] ?? 0;
    return Number(value) || 0;
}

function dateParam(value) {
    return value && dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : '';
}

function MetricLabel({ label, metricKey }) {
    const title = metricDefinitions[metricKey];

    return (
        <span className={styles.metricLabel}>
            <span>{label}</span>
            {title ? (
                <Tooltip title={title}>
                    <span className={styles.infoMark}>?</span>
                </Tooltip>
            ) : null}
        </span>
    );
}

function MetricCard({ card }) {
    return (
        <div className={styles.metricCard}>
            <div className={styles.metricTitle}>
                <MetricLabel label={card.title} metricKey={card.metricKey} />
            </div>
            <div className={styles.metricValueRow}>
                <div className={styles.metricValue}>{card.value}</div>
                {card.note && <div className={styles.metricNote}>{card.note}</div>}
            </div>
        </div>
    );
}

function CoefficientBadge({ value }) {
    return <span className={`${styles.coefficientBadge} ${coefficientTone(value)}`}>{formatCoefficient(value)}</span>;
}

function KpiScale({ title, rows = [] }) {
    return (
        <div className={styles.kpiScale}>
            <strong>{title}</strong>
            <div className={styles.kpiScaleRows}>
                {rows.map((row) => (
                    <div className={`${styles.kpiScaleRow} ${coefficientTone(row.coefficient)}`} key={`${title}-${row.label}`}>
                        <span>{row.label}</span>
                        <b>{formatCoefficient(row.coefficient)}</b>
                    </div>
                ))}
            </div>
        </div>
    );
}

function ItTpKpiPanel({ config }) {
    if (!config) return null;

    return (
        <div className={styles.kpiPanel}>
            <div className={styles.kpiPanelHeader}>
                <div>
                    <h3>KPI команды IT ТП</h3>
                    <p>{config.formula_label}</p>
                </div>
                <span>Для расчёта нужны оба показателя</span>
            </div>
            <div className={styles.kpiScales}>
                <KpiScale title="Среднее время закрытия" rows={config.close_time_scale} />
                <KpiScale title="Средняя оценка" rows={config.rating_scale} />
            </div>
        </div>
    );
}

function KpiTicketSamples({ title, average, unit, tickets = [], dateKey }) {
    const [sort, setSort] = useState({ key: 'date', direction: 'desc' });
    const sortedTickets = useMemo(() => [...tickets].sort((left, right) => {
        let leftValue;
        let rightValue;
        if (sort.key === 'ticket') {
            leftValue = Number(left.task_id) || 0;
            rightValue = Number(right.task_id) || 0;
        } else if (sort.key === 'value') {
            leftValue = Number(left.value) || 0;
            rightValue = Number(right.value) || 0;
        } else {
            leftValue = left[dateKey] ? dayjs(left[dateKey]).valueOf() : 0;
            rightValue = right[dateKey] ? dayjs(right[dateKey]).valueOf() : 0;
        }
        const comparison = leftValue === rightValue ? 0 : leftValue > rightValue ? 1 : -1;
        return sort.direction === 'asc' ? comparison : -comparison;
    }), [dateKey, sort, tickets]);
    const changeSort = (key) => setSort((current) => ({
        key,
        direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
    }));
    const sortHeader = (label, key) => (
        <button type="button" className={styles.kpiSortButton} onClick={() => changeSort(key)}>
            <span>{label}</span>
            <span className={styles.kpiSortArrow}>{sort.key === key ? (sort.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
        </button>
    );

    return (
        <div className={styles.kpiDetailGroup}>
            <div className={styles.kpiDetailHeading}>
                <strong>{title}</strong>
                <span>Среднее: {formatNumber(average, average === null ? '' : unit)} · тикетов: {tickets.length}</span>
            </div>
            {tickets.length === 0 ? (
                <p className={styles.kpiDetailEmpty}>Нет значений за выбранный период</p>
            ) : (
                <div className={styles.kpiDetailTableWrap}>
                    <table className={styles.kpiDetailTable}>
                        <thead>
                            <tr>
                                <th aria-sort={sort.key === 'ticket' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>{sortHeader('Тикет', 'ticket')}</th>
                                <th aria-sort={sort.key === 'value' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>{sortHeader('Значение', 'value')}</th>
                                <th aria-sort={sort.key === 'date' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>{sortHeader('Дата', 'date')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTickets.map((ticket, index) => (
                                <tr key={`${title}-${ticket.task_id}-${index}`}>
                                    <td><a className={styles.taskLink} href={`/task/${ticket.task_id}`}>#{ticket.task_id} {ticket.title || 'Без темы'}</a></td>
                                    <td>{formatNumber(ticket.value, unit)}</td>
                                    <td>{formatAnalyticsDate(ticket[dateKey])}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function MemberKpiDetails({ member }) {
    const details = member.kpi_details || {};
    const closeTime = details.close_time || {};
    const rating = details.rating || {};

    return (
        <div className={styles.kpiDetails}>
            <KpiTicketSamples
                title="Время закрытия"
                average={closeTime.average}
                unit=" ч"
                tickets={Array.isArray(closeTime.tickets) ? closeTime.tickets : []}
                dateKey="closed_at"
            />
            <KpiTicketSamples
                title="Оценки"
                average={rating.average}
                unit=" / 5"
                tickets={Array.isArray(rating.tickets) ? rating.tickets : []}
                dateKey="submitted_at"
            />
        </div>
    );
}

function SlaBucket({ title, metricKey, bucket }) {
    const rows = ['normal', 'risk', 'overdue', 'done', 'paused'];

    return (
        <div className={styles.tableWrap}>
            <table className={styles.compactTable}>
                <thead>
                    <tr>
                        <th colSpan={3}><MetricLabel label={title} metricKey={metricKey} /></th>
                    </tr>
                    <tr>
                        <th>Состояние</th>
                        <th>Кол-во</th>
                        <th>%</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((state) => (
                        <tr key={state}>
                            <td>{slaStateLabels[state]}</td>
                            <td>{formatNumber(bucket?.[state]?.count)}</td>
                            <td>{formatPercent(bucket?.[state]?.percent)}</td>
                        </tr>
                    ))}
                    <tr className={styles.summaryRow}>
                        <td>Всего</td>
                        <td>{formatNumber(bucket?.total)}</td>
                        <td>100%</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function BreakdownTable({ title, rows, repeat = false }) {
    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>{title}</h2>
            </div>
            <div className={styles.tableWrap}>
                <table className={`${styles.table} ${styles.breakdownTable}`}>
                    <thead>
                        <tr>
                            <th>Группа</th>
                            <th><MetricLabel label="Всего" metricKey="breakdown_total" /></th>
                            <th><MetricLabel label="Создано" metricKey="breakdown_created" /></th>
                            <th><MetricLabel label="Открыто" metricKey="breakdown_open" /></th>
                            <th><MetricLabel label="Закрыто" metricKey="breakdown_closed" /></th>
                            <th><MetricLabel label="SLA просрочен" metricKey="breakdown_sla_overdue" /></th>
                            {repeat && <th><MetricLabel label="Повторы" metricKey="breakdown_repeat_count" /></th>}
                            <th><MetricLabel label="Среднее закрытие" metricKey="breakdown_avg_close_hours" /></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={repeat ? 8 : 7} className={styles.empty}>Нет данных</td>
                            </tr>
                        )}
                        {rows.map((row) => (
                            <tr key={row.key}>
                                <td>{row.title || row.key}</td>
                                <td>{formatNumber(row.total)}</td>
                                <td>{formatNumber(row.created)}</td>
                                <td>{formatNumber(row.open)}</td>
                                <td>{formatNumber(row.closed)}</td>
                                <td>{formatNumber(row.sla_overdue)}</td>
                                {repeat && <td>{formatNumber(row.repeat_count)}</td>}
                                <td>{formatNumber(row.avg_close_hours, row.avg_close_hours === null ? '' : ' ч')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function RatingDistribution({ distribution }) {
    const scores = [1, 2, 3, 4, 5];
    const total = scores.reduce((sum, score) => sum + ratingDistributionValue(distribution, score), 0);

    return (
        <div className={styles.tableWrap}>
            <table className={styles.compactTable}>
                <thead>
                    <tr>
                        <th colSpan={3}><MetricLabel label="Распределение оценок" metricKey="rating_score_distribution" /></th>
                    </tr>
                    <tr>
                        <th>Оценка</th>
                        <th>Кол-во</th>
                        <th>%</th>
                    </tr>
                </thead>
                <tbody>
                    {scores.map((score) => {
                        const count = ratingDistributionValue(distribution, score);
                        const percent = total > 0 ? Math.round((count / total) * 1000) / 10 : 0;

                        return (
                            <tr key={score}>
                                <td><span className={styles.ratingBadge}>{score}</span></td>
                                <td>{formatNumber(count)}</td>
                                <td>{formatPercent(percent)}</td>
                            </tr>
                        );
                    })}
                    <tr className={styles.summaryRow}>
                        <td>Всего</td>
                        <td>{formatNumber(total)}</td>
                        <td>{total > 0 ? '100%' : '0%'}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function RatingCommentsTable({ comments }) {
    const rows = Array.isArray(comments) ? comments : [];

    return (
        <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.ratingCommentsTable}`}>
                <thead>
                    <tr>
                        <th>Тикет</th>
                        <th>Оценка</th>
                        <th>Исполнитель</th>
                        <th>Команда</th>
                        <th>Дата</th>
                        <th>Комментарий</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={6} className={styles.empty}>Комментариев к оценкам за выбранный период нет</td>
                        </tr>
                    )}
                    {rows.map((item) => (
                        <tr key={`${item.taskId}-${item.submittedAt || ''}`}>
                            <td>
                                <div className={styles.ticketCell}>
                                    <a href={`/task/${item.taskId}`} className={styles.taskLink}>
                                        #{item.taskId}
                                    </a>
                                    <span>{item.taskTitle || 'Без названия'}</span>
                                </div>
                            </td>
                            <td><span className={styles.ratingBadge}>{formatNumber(item.score)}</span></td>
                            <td>{item.assigneeName || '—'}</td>
                            <td>{item.teamTitle || '—'}</td>
                            <td>{item.submittedAt || '—'}</td>
                            <td className={styles.commentCell}>{item.comment || '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function AnalyticsPage() {
    const initialRange = useMemo(() => [dayjs().subtract(29, 'day'), dayjs()], []);
    const [dateRange, setDateRange] = useState(initialRange);
    const [teamId, setTeamId] = useState(0);
    const [loading, setLoading] = useState(false);
    const [overall, setOverall] = useState(null);
    const [previousDay, setPreviousDay] = useState(null);
    const [dailyLabels, setDailyLabels] = useState([]);
    const [dailyTotals, setDailyTotals] = useState([]);
    const [members, setMembers] = useState([]);
    const [availableTeams, setAvailableTeams] = useState([]);
    const [canExcludeMyTasks, setCanExcludeMyTasks] = useState(false);
    const [excludeMyTasks, setExcludeMyTasks] = useState(false);
    const [periodInfo, setPeriodInfo] = useState({ days: 30 });
    const [ratingComments, setRatingComments] = useState([]);
    const [itTpKpi, setItTpKpi] = useState(null);
    const [expandedKpiMembers, setExpandedKpiMembers] = useState(() => new Set());
    const [breakdowns, setBreakdowns] = useState({
        categories: [],
        subcategories: [],
        channels: [],
        priorities: [],
        terminals: [],
        companies: [],
    });

    const fetchAnalytics = async (range = dateRange, selectedTeamId = teamId, excludeMine = excludeMyTasks) => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (Array.isArray(range) && range[0] && range[1]) {
                params.set('dateFrom', dateParam(range[0]));
                params.set('dateTo', dateParam(range[1]));
            } else {
                params.set('periodDays', String(periodInfo.days || 30));
            }
            if (Number(selectedTeamId) > 0) {
                params.set('teamId', String(selectedTeamId));
            }
            if (excludeMine) {
                params.set('excludeMyTasks', '1');
            }

            const res = await api.get(`/api/analytics/tasks?${params.toString()}`);
            setOverall(res.data?.overall || null);
            setPreviousDay(res.data?.previous_day || null);
            setDailyLabels(Array.isArray(res.data?.daily_labels) ? res.data.daily_labels : []);
            setDailyTotals(Array.isArray(res.data?.daily_totals) ? res.data.daily_totals : []);
            setMembers(Array.isArray(res.data?.members) ? res.data.members : []);
            setRatingComments(Array.isArray(res.data?.rating_comments) ? res.data.rating_comments : []);
            setItTpKpi(res.data?.it_tp_kpi || null);
            setExpandedKpiMembers(new Set());
            setAvailableTeams(Array.isArray(res.data?.available_teams) ? res.data.available_teams : []);
            setCanExcludeMyTasks(Boolean(res.data?.can_exclude_my_tasks));
            setExcludeMyTasks(Boolean(res.data?.exclude_my_tasks));
            setTeamId(Number(res.data?.selected_team_id) || 0);
            setPeriodInfo(res.data?.period || { days: res.data?.period_days || 30 });
            setBreakdowns({
                categories: Array.isArray(res.data?.breakdowns?.categories) ? res.data.breakdowns.categories : [],
                subcategories: Array.isArray(res.data?.breakdowns?.subcategories) ? res.data.breakdowns.subcategories : [],
                channels: Array.isArray(res.data?.breakdowns?.channels) ? res.data.breakdowns.channels : [],
                priorities: Array.isArray(res.data?.breakdowns?.priorities) ? res.data.breakdowns.priorities : [],
                terminals: Array.isArray(res.data?.breakdowns?.terminals) ? res.data.breakdowns.terminals : [],
                companies: Array.isArray(res.data?.breakdowns?.companies) ? res.data.breakdowns.companies : [],
            });
        } catch (e) {
            console.error('Ошибка загрузки аналитики:', e);
            setOverall(null);
            setPreviousDay(null);
            setDailyLabels([]);
            setDailyTotals([]);
            setMembers([]);
            setRatingComments([]);
            setItTpKpi(null);
            setExpandedKpiMembers(new Set());
            setAvailableTeams([]);
            setBreakdowns({ categories: [], subcategories: [], channels: [], priorities: [], terminals: [], companies: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics(initialRange, 0, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const teamOptions = useMemo(() => ([
        { value: 0, label: 'Все команды' },
        ...availableTeams.map((team) => ({
            value: Number(team.id),
            label: team.title || team.code || `Команда #${team.id}`,
        })),
    ]), [availableTeams]);

    const cards = useMemo(() => {
        if (!overall) return [];
        const result = [
            { metricKey: 'tasks_total', title: 'Всего задач', value: formatNumber(overall.tasks_total) },
            { metricKey: 'open_tasks_now', title: 'В работе сейчас', value: formatNumber(overall.open_tasks_now) },
            { metricKey: 'waiting_partner_now', title: 'Ждет партнера', value: formatNumber(overall.waiting_partner_now) },
            { metricKey: 'closed_tasks_period', title: 'Закрыто за период', value: formatNumber(overall.closed_tasks_period) },
            { metricKey: 'avg_first_response_minutes', title: 'Средний 1-й ответ', value: formatNumber(overall.avg_first_response_minutes, ' мин') },
            { metricKey: 'avg_reply_after_author_minutes', title: 'Ответ после автора', value: formatNumber(overall.avg_reply_after_author_minutes, ' мин') },
            { metricKey: 'avg_close_hours', title: 'Среднее закрытие', value: formatNumber(overall.avg_close_hours, ' ч') },
            { metricKey: 'rating_avg_score', title: 'Средняя оценка', value: formatNumber(overall.rating_avg_score, overall.rating_avg_score === null ? '' : ' / 5') },
            { metricKey: 'ratings_submitted_period', title: 'Оценок за период', value: formatNumber(overall.ratings_submitted_period) },
            { metricKey: 'rating_comments_period', title: 'Комментариев к оценкам', value: formatNumber(overall.rating_comments_period) },
            { metricKey: 'rating_pending_now', title: 'Ожидают оценки', value: formatNumber(overall.rating_pending_now) },
            { metricKey: 'activity_actions_period', title: 'Действий за период', value: formatNumber(overall.activity_actions_period) },
            { metricKey: 'staff_without_activity_period', title: 'Без действий за период', value: formatNumber(overall.staff_without_activity_period) },
        ];
        if (itTpKpi) {
            result.splice(8, 0, {
                metricKey: 'kpi_coefficient',
                title: 'KPI команды IT ТП',
                value: formatCoefficient(overall.kpi_coefficient),
            });
        }
        return result;
    }, [itTpKpi, overall]);

    const queueCards = useMemo(() => {
        if (!overall) return [];
        return [
            { metricKey: 'waiting_staff_now', title: 'Ждут ответа ТП', value: formatNumber(overall.waiting_staff_now) },
            { metricKey: 'waiting_staff_over_2h', title: 'Ждут > 2ч', value: formatNumber(overall.waiting_staff_over_2h) },
            { metricKey: 'waiting_staff_over_24h', title: 'Ждут > 24ч', value: formatNumber(overall.waiting_staff_over_24h) },
            { metricKey: 'unassigned_now', title: 'Без исполнителя', value: formatNumber(overall.unassigned_now) },
            { metricKey: 'no_first_response_now', title: 'Без первого ответа', value: formatNumber(overall.no_first_response_now) },
            { metricKey: 'inactivity_now', title: 'Бездействие', value: formatNumber(overall.inactivity_now) },
            { metricKey: 'terminal_payment_blocked_now', title: 'Терминал не принимает оплаты', value: formatNumber(overall.terminal_payment_blocked_now) },
            { metricKey: 'sla_overdue_total', title: 'Просрочка SLA', value: formatNumber(overall.sla_overdue_total) },
            { metricKey: 'sla_compliance_percent', title: 'Соблюдение SLA', value: formatPercent(overall.sla_compliance_percent) },
            { metricKey: 'repeat_terminal_tasks_period', title: 'Повторы терминалов', value: formatNumber(overall.repeat_terminal_tasks_period) },
            { metricKey: 'repeat_company_tasks_period', title: 'Повторы филиалов', value: formatNumber(overall.repeat_company_tasks_period) },
            { metricKey: 'reopened_tasks_period', title: 'Переоткрытые', value: formatNumber(overall.reopened_tasks_period) },
            { metricKey: 'reassigned_tasks_period', title: 'Переназначенные', value: formatNumber(overall.reassigned_tasks_period) },
            { metricKey: 'avg_status_time_hours', title: 'Время в статусах', value: formatNumber(overall.avg_status_time_hours, ' ч') },
        ];
    }, [overall]);

    const previousDayCards = useMemo(() => {
        if (!previousDay) return [];
        return [
            { metricKey: 'incoming_tickets', title: 'Пришло тикетов', value: formatNumber(previousDay.incoming_tickets) },
            { metricKey: 'resolved_tickets', title: 'Решено тикетов', value: formatNumber(previousDay.resolved_tickets) },
            { metricKey: 'unresolved_within_day', title: 'Не решено в рамках дня', value: formatNumber(previousDay.unresolved_within_day) },
            { metricKey: 'in_work_total', title: 'В работе всего', value: formatNumber(previousDay.in_work_total) },
            { metricKey: 'waiting_partner_total', title: 'Ждет партнера', value: formatNumber(previousDay.waiting_partner_total) },
            { metricKey: 'terminal_down_total', title: 'Не работает терминал', value: formatNumber(previousDay.terminal_down_total) },
            {
                metricKey: 'tickets_over_14_days',
                title: 'Тикеты более 14 дней',
                value: formatNumber(previousDay.tickets_over_14_days),
                note: formatClosedCount(previousDay.tickets_over_14_days_closed),
            },
            {
                metricKey: 'tickets_over_30_days',
                title: 'Тикеты более 30 дней',
                value: formatNumber(previousDay.tickets_over_30_days),
                note: formatClosedCount(previousDay.tickets_over_30_days_closed),
            },
        ];
    }, [previousDay]);

    return (
        <ContentWMax>
            <PageTitle title="Аналитика" />

            <div className={styles.page}>
                {loading && <LoadingOverlay text="Загружаем аналитику..." />}
                <div className={styles.toolbar}>
                    <RangePicker
                        value={dateRange}
                        onChange={(value) => setDateRange(value)}
                        presets={datePresets}
                        format="DD.MM.YYYY"
                        className={styles.rangePicker}
                    />
                    <Select
                        value={teamId}
                        onChange={(value) => setTeamId(Number(value) || 0)}
                        options={teamOptions}
                        className={styles.teamSelect}
                    />
                    {canExcludeMyTasks && (
                        <Checkbox
                            checked={excludeMyTasks}
                            onChange={(event) => {
                                const checked = Boolean(event?.target?.checked);
                                setExcludeMyTasks(checked);
                                fetchAnalytics(dateRange, teamId, checked);
                            }}
                            className={styles.toolbarCheckbox}
                        >
                            Не учитывать мои задачи
                        </Checkbox>
                    )}
                    <Button
                        type="primary"
                        loading={loading}
                        onClick={() => fetchAnalytics(dateRange, teamId, excludeMyTasks)}
                    >
                        Обновить
                    </Button>
                </div>
                <p className={styles.sectionHint}>
                    Временные метрики считаются только в рабочие часы ТП: 08:00-20:00. Период: {periodInfo?.date_from || '—'} - {periodInfo?.date_to || '—'}.
                </p>

                <div className={styles.metricGrid}>
                    {cards.map((card) => <MetricCard key={card.metricKey} card={card} />)}
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Очередь и SLA</h2>
                    </div>
                    <div className={styles.metricGrid}>
                        {queueCards.map((card) => <MetricCard key={card.metricKey} card={card} />)}
                    </div>
                    <div className={styles.slaGrid}>
                        <SlaBucket title="SLA первого ответа" metricKey="sla_first_response" bucket={overall?.sla_first_response} />
                        <SlaBucket title="SLA решения" metricKey="sla_resolution" bucket={overall?.sla_resolution} />
                        <div className={styles.tableWrap}>
                            <table className={styles.compactTable}>
                                <thead>
                                    <tr>
                                        <th colSpan={2}><MetricLabel label="Возраст открытых тикетов" metricKey="age_buckets" /></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(Array.isArray(overall?.age_buckets) ? overall.age_buckets : []).map((bucket) => (
                                        <tr key={bucket.key}>
                                            <td>{bucket.title}</td>
                                            <td>{formatNumber(bucket.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {previousDay && (
                    <div className={styles.section}>
                        <div className={styles.sectionHeader}>
                            <h2 className={styles.sectionTitle}>Сводка за {previousDay.label}</h2>
                            <p className={styles.sectionHint}>Показатели по последнему завершенному дню в выбранном диапазоне</p>
                        </div>

                        <div className={styles.metricGrid}>
                            {previousDayCards.map((card) => <MetricCard key={card.metricKey} card={card} />)}
                        </div>
                    </div>
                )}

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Оценки закрытия</h2>
                        <p className={styles.sectionHint}>Период считается по дате отправки оценки</p>
                    </div>
                    <div className={styles.ratingGrid}>
                        <RatingDistribution distribution={overall?.rating_score_distribution} />
                        <RatingCommentsTable comments={ratingComments} />
                    </div>
                </div>

                <BreakdownTable title="Категории" rows={breakdowns.categories} />
                <BreakdownTable title="Подкатегории" rows={breakdowns.subcategories} />
                <BreakdownTable title="Каналы" rows={breakdowns.channels} />
                <BreakdownTable title="Приоритеты" rows={breakdowns.priorities} />
                <BreakdownTable title="Топ терминалов" rows={breakdowns.terminals} repeat />
                <BreakdownTable title="Топ филиалов" rows={breakdowns.companies} repeat />

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Сотрудники</h2>
                    </div>
                    <ItTpKpiPanel config={itTpKpi} />
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Сотрудник</th>
                                    {itTpKpi && <th><MetricLabel label="KPI" metricKey="kpi_coefficient" /></th>}
                                    <th><MetricLabel label="В работе" metricKey="open_tasks_now" /></th>
                                    <th><MetricLabel label="Ждет партнера" metricKey="waiting_partner_now" /></th>
                                    <th><MetricLabel label="Закрыто за период" metricKey="closed_tasks_period" /></th>
                                    <th><MetricLabel label="Средняя оценка" metricKey="rating_avg_score" /></th>
                                    {itTpKpi && <th><MetricLabel label="Коэф. оценки" metricKey="rating_coefficient" /></th>}
                                    <th><MetricLabel label="Оценок" metricKey="ratings_submitted_period" /></th>
                                    <th><MetricLabel label="Комментариев" metricKey="rating_comments_period" /></th>
                                    <th><MetricLabel label="Скорость закрытия" metricKey="close_rate_per_day" /></th>
                                    <th><MetricLabel label="1-й ответ" metricKey="avg_first_response_minutes" /></th>
                                    <th><MetricLabel label="Ответ после автора" metricKey="avg_reply_after_author_minutes" /></th>
                                    <th><MetricLabel label="Среднее закрытие" metricKey="avg_close_hours" /></th>
                                    {itTpKpi && <th><MetricLabel label="Коэф. времени" metricKey="close_time_coefficient" /></th>}
                                    <th><MetricLabel label="Ждут ответа" metricKey="waiting_staff_now" /></th>
                                    <th><MetricLabel label="Ждут >2ч" metricKey="waiting_staff_over_2h" /></th>
                                    <th><MetricLabel label="Ждут >24ч" metricKey="waiting_staff_over_24h" /></th>
                                    <th><MetricLabel label="Без 1-го ответа" metricKey="no_first_response_now" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length === 0 && (
                                    <tr>
                                        <td colSpan={itTpKpi ? 18 : 15} className={styles.empty}>Нет данных за выбранный период</td>
                                    </tr>
                                )}
                                {members.map((member) => (
                                    <React.Fragment key={member.user_id}>
                                    <tr>
                                        <td>
                                            <div className={styles.memberCell}>
                                                <span>{member.name || '—'}</span>
                                                {itTpKpi && (
                                                    <button
                                                        type="button"
                                                        className={styles.kpiDetailsButton}
                                                        aria-expanded={expandedKpiMembers.has(member.user_id)}
                                                        onClick={() => setExpandedKpiMembers((current) => {
                                                            const next = new Set(current);
                                                            if (next.has(member.user_id)) next.delete(member.user_id);
                                                            else next.add(member.user_id);
                                                            return next;
                                                        })}
                                                    >
                                                        {expandedKpiMembers.has(member.user_id) ? 'Скрыть расчёт' : 'Показать расчёт'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        {itTpKpi && <td><CoefficientBadge value={member.kpi_coefficient} /></td>}
                                        <td>{formatNumber(member.open_tasks_now)}</td>
                                        <td>{formatNumber(member.waiting_partner_now)}</td>
                                        <td>{formatNumber(member.closed_tasks_period)}</td>
                                        <td>{formatNumber(member.rating_avg_score, member.rating_avg_score === null ? '' : ' / 5')}</td>
                                        {itTpKpi && <td><CoefficientBadge value={member.rating_coefficient} /></td>}
                                        <td>{formatNumber(member.ratings_submitted_period)}</td>
                                        <td>{formatNumber(member.rating_comments_period)}</td>
                                        <td>{formatNumber(member.close_rate_per_day)}</td>
                                        <td>{formatNumber(member.avg_first_response_minutes)}</td>
                                        <td>{formatNumber(member.avg_reply_after_author_minutes)}</td>
                                        <td>{formatNumber(member.avg_close_hours)}</td>
                                        {itTpKpi && <td><CoefficientBadge value={member.close_time_coefficient} /></td>}
                                        <td>{formatNumber(member.waiting_staff_now)}</td>
                                        <td>{formatNumber(member.waiting_staff_over_2h)}</td>
                                        <td>{formatNumber(member.waiting_staff_over_24h)}</td>
                                        <td>{formatNumber(member.no_first_response_now)}</td>
                                    </tr>
                                    {itTpKpi && expandedKpiMembers.has(member.user_id) && (
                                        <tr className={styles.kpiDetailsRow}>
                                            <td colSpan={18}><MemberKpiDetails member={member} /></td>
                                        </tr>
                                    )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Загрузка сотрудников по дням</h2>
                        <p className={styles.sectionHint}>В ячейке: задач в работе на конец дня. Ниже: пришло / решено и ожидание партнера.</p>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={`${styles.table} ${styles.dailyTable}`}>
                            <thead>
                                <tr>
                                    <th>Сотрудник</th>
                                    {dailyLabels.map((day) => (
                                        <th key={day.date}>{day.label}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {dailyTotals.length > 0 && (
                                    <tr className={styles.summaryRow}>
                                        <td>Команда</td>
                                        {dailyTotals.map((day) => (
                                            <td key={`team-${day.date}`}>
                                                <div className={styles.dailyCell}>
                                                    <div className={styles.dailyMain}>{formatNumber(day.open_end)}</div>
                                                    <div className={styles.dailyHint}>+{formatNumber(day.created)} / -{formatNumber(day.closed)}</div>
                                                    <div className={styles.dailyHint}>ждет партнера: {formatNumber(day.waiting_partner_end)}</div>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                )}

                                {members.length === 0 && (
                                    <tr>
                                        <td colSpan={dailyLabels.length + 1} className={styles.empty}>Нет данных за выбранный период</td>
                                    </tr>
                                )}

                                {members.map((member) => (
                                    <tr key={`daily-${member.user_id}`}>
                                        <td>{member.name || '—'}</td>
                                        {(Array.isArray(member.daily_load) ? member.daily_load : []).map((day) => (
                                            <td key={`${member.user_id}-${day.date}`}>
                                                <div className={styles.dailyCell}>
                                                    <div className={styles.dailyMain}>{formatNumber(day.open_end)}</div>
                                                    <div className={styles.dailyHint}>+{formatNumber(day.created)} / -{formatNumber(day.closed)}</div>
                                                    <div className={styles.dailyHint}>ждет партнера: {formatNumber(day.waiting_partner_end)}</div>
                                                </div>
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <h2 className={styles.sectionTitle}>Действия сотрудников</h2>
                        <p className={styles.sectionHint}>Audit-log за выбранный период</p>
                    </div>

                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Сотрудник</th>
                                    <th><MetricLabel label="Всего действий" metricKey="activity_actions_period" /></th>
                                    <th><MetricLabel label="Задач затронуто" metricKey="activity_tasks_touched" /></th>
                                    <th><MetricLabel label="Открыл" metricKey="activity_task_views" /></th>
                                    <th><MetricLabel label="Изменил задачу" metricKey="activity_task_updates" /></th>
                                    <th><MetricLabel label="Добавил сообщение" metricKey="activity_messages_created" /></th>
                                    <th><MetricLabel label="Изменил сообщение" metricKey="activity_messages_updated" /></th>
                                    <th><MetricLabel label="Удалил сообщение" metricKey="activity_messages_deleted" /></th>
                                    <th><MetricLabel label="Последнее действие" metricKey="activity_last_at" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className={styles.empty}>Нет сотрудников для отображения</td>
                                    </tr>
                                )}
                                {members.map((member) => (
                                    <tr key={`activity-${member.user_id}`}>
                                        <td>{member.name || '—'}</td>
                                        <td>{formatNumber(member.activity_total)}</td>
                                        <td>{formatNumber(member.activity_tasks_touched)}</td>
                                        <td>{formatNumber(member.activity_task_views)}</td>
                                        <td>{formatNumber(member.activity_task_updates)}</td>
                                        <td>{formatNumber(member.activity_messages_created)}</td>
                                        <td>{formatNumber(member.activity_messages_updated)}</td>
                                        <td>{formatNumber(member.activity_messages_deleted)}</td>
                                        <td>
                                            {member.activity_last_at ? (
                                                <div className={styles.activityCell}>
                                                    <div>{member.activity_last_at}</div>
                                                    <div>
                                                        <strong>{member.activity_last_action_label || 'Действие'}</strong>
                                                        {member.activity_last_task_id ? (
                                                            <>
                                                                {' · '}
                                                                <a href={`/task/${member.activity_last_task_id}`} className={styles.taskLink}>
                                                                    #{member.activity_last_task_id}
                                                                </a>
                                                            </>
                                                        ) : null}
                                                    </div>
                                                    {member.activity_last_description ? (
                                                        <div className={styles.activityHint}>{member.activity_last_description}</div>
                                                    ) : null}
                                                </div>
                                            ) : (
                                                <span className={styles.empty}>Нет действий за период</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ContentWMax>
    );
}
